import { useCallback, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { useSearchParams } from 'react-router';
import { Box, ButtonBase, Paper } from '@/shared/ui';

import FactsheetWindow from '@/features/factsheet/components/FactsheetView/FactsheetWindow';
import { FACTSHEET_TAB_PARAM_NAME } from '@/features/factsheet/constants';
import { removeAtIndex } from '@/features/factsheet/state/factsheetTabsReducer';
import { useFactsheetTabsState } from '@/features/factsheet/state/useFactsheetTabsState';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import { addStringIfNotExists } from '@/shared/lib/array/addStringIfNotExists';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';
import type { Sx } from '@/shared/types/uiTypes';
import { TooltipIconButton } from '@/shared/ui/primitives/IconButton';
import Tabs from '@/shared/ui/primitives/Tabs';
import TabPanel from '@/shared/ui/primitives/Tabs/TabPanel';

// Above this many open tabs, finding and closing each one individually gets
// tedious in the catalog's narrow panel, so a single "close all" action is
// offered instead. Only relevant in compact (catalog) mode.
const CLOSE_ALL_TABS_THRESHOLD = 4;

const paperSx: Sx = {
  width: 1024,
  maxWidth: '100%',
  mx: 8,
  my: 5,
  display: 'flex',
  flexDirection: 'column'
};
// Fills the claimed container (e.g. the catalog table) instead of centering
// as a fixed-width page, when the factsheet is rendered in the scoped panel.
const compactPaperSx: Sx = {
  width: '100%',
  maxWidth: '100%',
  mx: 0,
  my: 0,
  height: '100%'
};
const headerSx: Sx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 1.5,
  px: 3
};
const compactHeaderSx: Sx = {
  p: 0
};
const closeBtnSx = { width: 24, height: 24 };
const closeAllBtnSx: Sx = {
  width: 32,
  minWidth: 32,
  height: 36,
  backgroundColor: 'transparent',
  ':hover': {
    backgroundColor: 'action.hover'
  }
};
const logoLinkSx = { display: 'flex' };
const logoImgSize = { width: 130, height: 40 };
const tabPanelSx = {
  display: 'flex',
  flexGrow: 1,
  flex: 1,
  height: '100%',
  width: '100%'
};

const FactsheetHeaderAction = ({ onDialogClose }: { onDialogClose?: () => void }) => {
  if (!onDialogClose) {
    return (
      <Box
        component='a'
        href='/'
        sx={logoLinkSx}
      >
        <img
          alt='logoGx'
          src='/gx-logo-white.svg'
          style={logoImgSize}
        />
      </Box>
    );
  }

  return (
    <ButtonBase
      aria-label='close'
      sx={closeBtnSx}
      onClick={event => {
        event.stopPropagation();
        onDialogClose();
      }}
    >
      <CloseIcon />
    </ButtonBase>
  );
};

const FactsheetView = ({
  factsheetKeys,
  onDialogClose,
  setAdditionalTabsParam,
  onOpenTabsChange
}: {
  factsheetKeys: string;
  onDialogClose?: () => void;
  setAdditionalTabsParam?: (tabs: string[]) => void;
  /** Called with the full ordered list of open codes (index 0 is the main one) whenever the set of open tabs changes, so the main tab can be replaced too (e.g. when it's the one being closed). */
  onOpenTabsChange?: (tabs: string[]) => void;
}) => {
  const isCompact = useIsFactsheetCompact();

  const [{ activeTab, openedFactsheets, tabNames }, dispatch] =
    useFactsheetTabsState(factsheetKeys);
  const [, setUrlSearchParams] = useSearchParams();

  // Functional updater: operates on the live search params instead of a
  // snapshot captured at render time, so it doesn't clobber an
  // additional-tabs/main-code update made earlier in the same handler (e.g.
  // by onOpenTabsChange) with a stale copy.
  const deleteActiveTabParam = useCallback(() => {
    setUrlSearchParams(prev => {
      prev.delete(FACTSHEET_TAB_PARAM_NAME);
      return prev;
    });
  }, [setUrlSearchParams]);

  const handleChangeTab = useCallback(
    (index: number) => {
      deleteActiveTabParam();
      dispatch({ type: 'changeTab', index });
    },
    [deleteActiveTabParam, dispatch]
  );

  const onClose = useCallback(
    (indexToClose: number) => {
      // Closing the last remaining tab closes the whole panel instead of
      // leaving an empty header behind.
      if (openedFactsheets.length === 1) {
        onDialogClose?.();
        return;
      }

      const updated = removeAtIndex(openedFactsheets, indexToClose);
      setAdditionalTabsParam?.(updated.slice(1));
      dispatch({ type: 'closeTab', index: indexToClose });
      // onOpenTabsChange already clears the active-tab param as part of its
      // own (transition-wrapped) URL update. Firing deleteActiveTabParam's
      // separate, non-transition update in the same handler would race it and
      // can silently discard the transition's update, so only call it when
      // onOpenTabsChange isn't handling that already.
      if (onOpenTabsChange) {
        onOpenTabsChange(updated);
      } else {
        deleteActiveTabParam();
      }
    },
    [
      deleteActiveTabParam,
      dispatch,
      onDialogClose,
      onOpenTabsChange,
      openedFactsheets,
      setAdditionalTabsParam
    ]
  );

  const handleUpdateTabName = useCallback(
    (code: string, name: string) => {
      dispatch({ type: 'renameTab', code, name });
    },
    [dispatch]
  );

  const handleOpenNewTab = useCallback(
    (code: string) => {
      const isNewTab = !openedFactsheets.includes(code);
      if (isNewTab) {
        const updated = addStringIfNotExists(openedFactsheets, code);
        if (updated.length > 1) {
          setAdditionalTabsParam?.(updated.slice(1));
        }
        onOpenTabsChange?.(updated);
      }
      dispatch({ type: 'openTab', code });
    },
    [dispatch, onOpenTabsChange, openedFactsheets, setAdditionalTabsParam]
  );

  const handleCloseAll = useCallback(() => {
    onDialogClose?.();
  }, [onDialogClose]);

  const pageInformation = useMemo(() => {
    return {
      mainPageTitle: 'factsheets',
      subPageTitle: openedFactsheets[activeTab] ?? ''
    };
  }, [openedFactsheets, activeTab]);

  return (
    <PageInformationContext value={pageInformation}>
      <Paper sx={isCompact ? [paperSx, compactPaperSx] : paperSx}>
        <Box sx={isCompact ? [headerSx, compactHeaderSx] : headerSx}>
          <Tabs
            activeTab={activeTab}
            scrollButtons='auto'
            {...(isCompact && { minTabWidth: 0 })}
            tabs={openedFactsheets.map((code, index) => ({
              name: tabNames.get(code) ?? code,
              closable: isCompact || !!index,
              onClose
            }))}
            onChange={handleChangeTab}
          />
          {!isCompact && <FactsheetHeaderAction onDialogClose={onDialogClose} />}
          {isCompact && openedFactsheets.length > CLOSE_ALL_TABS_THRESHOLD && (
            <TooltipIconButton
              sx={closeAllBtnSx}
              title='Close all'
              onClick={handleCloseAll}
            >
              <CloseIcon />
            </TooltipIconButton>
          )}
        </Box>

        {openedFactsheets.map((code, index) => (
          <TabPanel
            key={code}
            containerSX={{
              flexGrow: 1,
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              ...(index === activeTab ? { display: 'flex' } : {})
            }}
            index={index}
            sx={tabPanelSx}
            value={activeTab}
          >
            <FactsheetWindow
              factsheetKey={code}
              openNewTab={handleOpenNewTab}
              updateTabName={handleUpdateTabName}
            />
          </TabPanel>
        ))}
      </Paper>
    </PageInformationContext>
  );
};
export default FactsheetView;
